import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close, CheckCircle, Error } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (cartId?: number) => void;
  onFailure?: () => void;
  variant: "cart" | "cartBar";
  paymentStatus: {
    status:
      | "idle"
      | "creating"
      | "processing"
      | "success"
      | "failed"
      | "cancelled";
    paymentUrl?: string;
    paymentId?: string;
    error?: string;
  };
  onConfirmPayment: (status: "PAYÉ" | "REFUSÉ") => void;
  onDeleteCart: () => void;
  onResetPayment: () => void;
  onCheckPaymentStatus?: () => void;
}

export default function PaymentModal({
  open,
  onClose,
  onSuccess,
  onFailure,
  variant,
  paymentStatus,
  onConfirmPayment,
  onDeleteCart,
  onResetPayment,
  onCheckPaymentStatus,
}: PaymentModalProps) {
  const { t } = useTranslation();
  const [webViewLoading, setWebViewLoading] = useState(false);
  const [webViewError, setWebViewError] = useState(false);
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle payment status changes
  useEffect(() => {
    if (paymentStatus.status === "success") {
      onSuccess?.(undefined);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } else if (paymentStatus.status === "failed") {
      onFailure?.();
    }
  }, [paymentStatus.status, onSuccess, onFailure]);

  const handleClose = () => {
    // Only delete cart if user explicitly cancels during payment
    // Don't delete cart just for closing the modal
    onResetPayment();
    setWebViewError(false);
    stopPolling();
    // Close popup window if open
    if (popupWindow && !popupWindow.closed) {
      popupWindow.close();
    }
    setPopupWindow(null);
    onClose();
  };

  const handleCancelPayment = () => {
    // This is for explicit cancellation - delete the cart
    if (paymentStatus.status === "processing") {
      onDeleteCart();
    }
    onResetPayment();
    setWebViewError(false);
    stopPolling();
    // Close popup window if open
    if (popupWindow && !popupWindow.closed) {
      popupWindow.close();
    }
    setPopupWindow(null);
    onClose();
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Open payment popup when processing starts
  useEffect(() => {
    if (
      paymentStatus.status === "processing" &&
      paymentStatus.paymentUrl &&
      !popupWindow
    ) {
      openPaymentPopup();
    }
  }, [paymentStatus.status, paymentStatus.paymentUrl, popupWindow]);

  const openPaymentPopup = () => {
    if (!paymentStatus.paymentUrl) return;

    // Calculate center position for popup
    const width = 800;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      paymentStatus.paymentUrl,
      "payment",
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=yes,toolbar=no,menubar=no,location=no`
    );

    if (popup) {
      setPopupWindow(popup);
      setWebViewLoading(false);
      setWebViewError(false);
      startPopupMonitoring(popup);
    } else {
      setWebViewError(true);
    }
  };

  const startPopupMonitoring = (popup: Window) => {
    pollingIntervalRef.current = setInterval(() => {
      try {
        if (popup && !popup.closed) {
          const currentUrl = popup.location.href;
          console.log("🔍 Checking popup URL:", currentUrl);

          if (currentUrl && currentUrl !== "about:blank") {
            // Check for PayTweak URL patterns (same as native app)
            if (
              currentUrl.includes("success") ||
              currentUrl.includes("payment-success") ||
              currentUrl.includes("p.success.php") ||
              currentUrl.includes("success.php")
            ) {
              console.log("✅ Payment success detected from URL:", currentUrl);
              onConfirmPayment("PAYÉ");
              stopPolling();
              popup.close();
            } else if (
              currentUrl.includes("p.fail.php") ||
              currentUrl.includes("error") ||
              currentUrl.includes("p.error.php") ||
              currentUrl.includes("refused") ||
              currentUrl.includes("p.refused.php") ||
              currentUrl.includes("error.php") ||
              currentUrl.includes("refused.php")
            ) {
              console.log("❌ Payment refused detected from URL:", currentUrl);
              onConfirmPayment("REFUSÉ");
              stopPolling();
              popup.close();
            } else if (
              currentUrl.includes("cancel") ||
              currentUrl.includes("p.cancel.php") ||
              currentUrl.includes("cancel.php")
            ) {
              console.log(
                "🚫 Payment cancelled detected from URL:",
                currentUrl
              );
              onDeleteCart();
              stopPolling();
              popup.close();
            } else if (currentUrl.includes("payinterim.aspx")) {
              console.log(
                "❌ PayTweak error page detected (payinterim.aspx) - payment failed"
              );
              onConfirmPayment("REFUSÉ");
              stopPolling();
              popup.close();
            }
          }
        } else {
          // Popup was closed manually
          console.log(
            "Popup closed manually - checking final payment status..."
          );
          if (onCheckPaymentStatus) {
            onCheckPaymentStatus();
          }
          stopPolling();
        }
      } catch (error) {
        // Cross-origin error - popup navigated to different domain
        console.log(
          "Cross-origin navigation detected - payment likely completed"
        );
        if (onCheckPaymentStatus) {
          onCheckPaymentStatus();
        }
        stopPolling();
      }
    }, 1000);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const renderContent = () => {
    if (paymentStatus.status === "creating") {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 4,
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("payment.creatingTitle", "Creating payment...")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("payment.creatingDesc", "Please wait while we prepare your payment.")}
          </Typography>
        </Box>
      );
    }

    if (paymentStatus.status === "processing" && paymentStatus.paymentUrl) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 4,
          }}
        >
          {webViewLoading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t("payment.openingPopup", "Opening payment window...")}
              </Typography>
            </Box>
          )}

          {webViewError && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Error sx={{ fontSize: 60, color: "error.main", mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: "error.main" }}>
                {t("payment.popupErrorTitle", "Open error")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, textAlign: "center" }}
              >
                {t("payment.popupErrorDesc", "Unable to open the payment window. Make sure popups are allowed.")}
              </Typography>
              <Button
                variant="contained"
                onClick={openPaymentPopup}
                sx={{ mb: 2 }}
              >
                {t("common.retry", "Retry")}
              </Button>
            </Box>
          )}

          {!webViewError && (
            <>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                {t("payment.processingTitle", "Payment in progress...")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, textAlign: "center" }}
              >
                {t("payment.processingDesc1", "A payment window has opened in the center of your screen.")}
                <br />
                {t("payment.processingDesc2", "Please complete the payment in that window.")}
                <br />
                {t("payment.processingDesc3", "The system automatically monitors the payment status.")}
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={() => popupWindow && popupWindow.focus()}
                sx={{ mb: 2 }}
                disabled={!popupWindow}
              >
                {popupWindow
                  ? t("payment.focusPopup", "Focus payment window")
                  : t("payment.openPaymentPage", "Open payment page")}
              </Button>

              {onCheckPaymentStatus && (
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={onCheckPaymentStatus}
                  sx={{ mb: 2 }}
                >
                  {t("payment.checkStatus", "Check payment status")}
                </Button>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                {t("payment.autoDetect", "The system automatically detects the payment result.")}
                <br />
                {t("payment.ifNotOpen", "If the window does not open, click the button above.")}
              </Typography>
            </>
          )}
        </Box>
      );
    }

    if (paymentStatus.status === "success") {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 4,
          }}
        >
          <CheckCircle sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: "success.main" }}>
            {t("payment.successTitle", "Payment successful!")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("payment.successDesc", "Your order has been confirmed.")}
          </Typography>
        </Box>
      );
    }

    if (paymentStatus.status === "failed") {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 4,
          }}
        >
          <Error sx={{ fontSize: 60, color: "error.main", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: "error.main" }}>
            {t("payment.failedTitle", "Payment failed")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {paymentStatus.error || t("payment.failedDesc", "An error occurred during payment.")}
          </Typography>
          <Button variant="contained" onClick={handleClose}>
            {t("common.close", "Close")}
          </Button>
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      PaperProps={{
        sx: { minHeight: "600px" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {variant === "cart" ? t("payment.conciergeTitle", "Concierge Payment") : t("payment.barTitle", "Bar Payment")}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>{renderContent()}</DialogContent>

      {paymentStatus.status === "processing" && (
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelPayment} color="secondary">
            {t("common.cancel", "Cancel")}
          </Button>
          {onCheckPaymentStatus && (
            <Button onClick={onCheckPaymentStatus} variant="outlined">
              {t("payment.checkStatus", "Check status")}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
